import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useGetProductsQuery, useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import {
  useCreateBomMutation,
  useUpdateBomMutation,
  useGetBomQuery,
} from '../../../store/api/bomApi';

function BOMForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const { data: existingBom, isLoading: loadingBom } = useGetBomQuery(id, { skip: !isEdit });
  const [createBom] = useCreateBomMutation();
  const [updateBom] = useUpdateBomMutation();

  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const { data: allProducts } = useGetProductsQuery({ limit: 100, productType: 'finished' });
  const { data: locationsData } = useGetLocationsQuery();

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [productId, setProductId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [yieldQty, setYieldQty] = useState(1);
  const [items, setItems] = useState([]);

  // Load existing BOM data
  useEffect(() => {
    if (isEdit && existingBom) {
      setName(existingBom.name || '');
      setNotes(existingBom.notes || '');
      setIsActive(existingBom.isActive ?? true);
      setProductId(existingBom.productId || '');
      setLocationId(existingBom.locationId || '');
      setYieldQty(existingBom.yield || 1);
      setItems(
        (existingBom.items || []).map((it) => ({
          ingredientProductId: it.ingredientProductId,
          productName: it.ingredient?.name,
          productSku: it.ingredient?.sku,
          quantity: it.quantity,
          unitOfMeasure: it.unitOfMeasure || '',
          costPrice: it.ingredient?.costPrice || 0,
        }))
      );
    }
  }, [isEdit, existingBom]);

  const handleAddIngredient = (product) => {
    if (!product) return;
    setItems((prev) => {
      const existing = prev.find((i) => i.ingredientProductId === product.id);
      if (existing) {
        alert('This ingredient is already in the recipe list.');
        return prev;
      }
      return [
        ...prev,
        {
          ingredientProductId: product.id,
          productName: product.name,
          productSku: product.sku,
          quantity: 1,
          unitOfMeasure: product.baseUnit || 'pcs',
          costPrice: product.costPrice || 0,
        },
      ];
    });
  };

  const handleRemoveIngredient = (index) => {
    setItems(items.filter((_, idx) => idx !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const calculateTotalCost = () => {
    return items.reduce((sum, item) => sum + parseFloat(item.quantity || 0) * parseFloat(item.costPrice || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !productId) {
      alert('Recipe Name and Menu Item are required');
      return;
    }
    if (items.length === 0) {
      alert('Please add at least one raw ingredient to the recipe.');
      return;
    }

    try {
      const payload = {
        name,
        productId,
        locationId: locationId || null,
        notes,
        yield: parseFloat(yieldQty) || 1,
        isActive,
        items: items.map((it) => ({
          ingredientProductId: it.ingredientProductId,
          quantity: parseFloat(it.quantity),
          unitOfMeasure: it.unitOfMeasure,
        })),
      };

      if (isEdit) {
        await updateBom({ id, ...payload }).unwrap();
      } else {
        await createBom(payload).unwrap();
      }

      navigate('/products/boms');
    } catch (err) {
      alert(err?.data?.message || 'Error saving recipe');
    }
  };

  if (isEdit && loadingBom) return <Typography sx={{ p: 3 }}>Loading Recipe...</Typography>;

  const finishedGoodsList = allProducts?.products || [];

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        {isEdit ? 'Edit Recipe (BOM)' : 'Create New Recipe (BOM)'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Recipe Name (e.g., Standard Beef Burger Recipe)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                fullWidth
                options={finishedGoodsList}
                getOptionLabel={(opt) => (opt ? `${opt.name} (${opt.sku})` : '')}
                value={finishedGoodsList.find((p) => p.id === productId) || null}
                onChange={(_, val) => setProductId(val?.id || '')}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => <TextField {...params} label="Finished Good / Menu Item" required />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                fullWidth
                options={locationsData || []}
                getOptionLabel={(opt) => opt.name || ''}
                value={(locationsData || []).find((l) => l.id === locationId) || null}
                onChange={(_, val) => setLocationId(val?.id || '')}
                renderInput={(params) => <TextField {...params} label="Kitchen Location (Optional)" />}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Preparation Instructions / Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Yield / Batch Size"
                helperText="How many finished items does this recipe create?"
                value={yieldQty}
                onChange={(e) => setYieldQty(e.target.value)}
                inputProps={{ min: 0.001, step: 0.001 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Active Recipe"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Ingredients Table */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recipe Ingredients & Composition
            </Typography>
            <Autocomplete
              sx={{ width: 350 }}
              size="small"
              options={productSearchData?.products || []}
              loading={searchingProducts}
              value={null}
              getOptionLabel={(opt) => `${opt.name} (${opt.sku})`}
              onInputChange={(_, value) => {
                if (value && value.trim().length >= 1) {
                  triggerProductSearch({ search: value.trim(), limit: 10 });
                }
              }}
              onChange={(_, value) => {
                if (value) handleAddIngredient(value);
              }}
              renderInput={(params) => <TextField {...params} label="Search Ingredients (Raw or Finished)..." />}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Ingredient Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unit of Measure</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Raw Material Cost (AED)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Required Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Line Total Cost (AED)</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No ingredients added yet. Please use the raw materials search above to add items.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => {
                    const lineTotal = parseFloat(item.quantity || 0) * parseFloat(item.costPrice || 0);

                    return (
                      <TableRow key={item.ingredientProductId} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{item.productName}</TableCell>
                        <TableCell>{item.productSku}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.unitOfMeasure}
                            onChange={(e) => handleItemChange(index, 'unitOfMeasure', e.target.value)}
                            placeholder="e.g. kg, g"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.costPrice}
                            onChange={(e) => handleItemChange(index, 'costPrice', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            inputProps={{ min: 0.001, step: 0.001 }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{lineTotal.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton size="small" color="error" onClick={() => handleRemoveIngredient(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Total Batch Cost:
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                AED {calculateTotalCost().toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Cost Per Unit ({finishedGoodsList.find(p => p.id === productId)?.baseUnit || 'unit'}):
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                AED {(calculateTotalCost() / (parseFloat(yieldQty) || 1)).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" size="large">
            {isEdit ? 'Update Recipe' : 'Create Recipe'}
          </Button>
          <Button variant="outlined" size="large" onClick={() => navigate('/products/boms')}>
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default BOMForm;
